/**
 * The product surface a given BigQuery query belongs to, recorded on every
 * `bigquery_usage` row.
 *
 * A fingerprint groups executions of one statement, but it cannot say which
 * product surface asked for it — several surfaces run the same builder with
 * different filters, and a fingerprint is opaque to anyone reading the admin
 * breakdown. Values are stable identifiers: renaming one splits its history in
 * the usage table, so add a new entry rather than editing an existing value.
 */
export const BIGQUERY_USAGE_FEATURES = {
    // ── Workspace worker: the four analytics pages ──
    DashboardMyBrand: 'dashboard-my-brand',
    OverviewMyBrand: 'overview-my-brand',
    OverviewCompetitors: 'overview-competitors',
    OverviewTopicDetail: 'overview-topic-detail',
    OverviewCompetitorTopicDetail: 'overview-competitor-topic-detail',
    DrilldownMyBrand: 'drilldown-my-brand',
    DrilldownMyBrandDrawer: 'drilldown-my-brand-drawer',
    DrilldownCompetitors: 'drilldown-competitors',
    DrilldownCreativeHash: 'drilldown-creative-hash',
    WorkspaceOptions: 'workspace-options',

    // ── Opportunities engine ──
    SetGeneration: 'set-generation',
    ScriptGeneration: 'script-generation',
    CreativesGeneration: 'creatives-generation',
    PersonaOpportunities: 'persona-opportunities',

    // ── Data sync pipelines ──
    DataSyncProducts: 'data-sync-products',
    DataSyncProductTypes: 'data-sync-product-types',
    DataSyncIndustries: 'data-sync-industries',
    DataSyncPersonas: 'data-sync-personas',
    DataSyncIndustryPersonas: 'data-sync-industry-personas',
    DataSyncFeaturesMappings: 'data-sync-features-mappings',
    BrandBrain: 'brand-brain',

    // ── Dashboard KPI package ──
    DashboardKpisMyBrand: 'dashboard-kpis-my-brand',
    DashboardKpisCompetitors: 'dashboard-kpis-competitors',
    DashboardKpisDefaultFilters: 'dashboard-kpis-default-filters',
    DashboardKpisIndustryProducts: 'dashboard-kpis-industry-products',

    // ── Core personalities onboarding ──
    CorePersonalities: 'core-personalities',
} as const;

export type BigQueryUsageFeature =
    (typeof BIGQUERY_USAGE_FEATURES)[keyof typeof BIGQUERY_USAGE_FEATURES];

export const BIGQUERY_USAGE_FEATURE_VALUES = Object.values(
    BIGQUERY_USAGE_FEATURES,
) as BigQueryUsageFeature[];

export function isBigQueryUsageFeature(value: string): value is BigQueryUsageFeature {
    return (BIGQUERY_USAGE_FEATURE_VALUES as string[]).includes(value);
}

/**
 * The service or shared package that owns each feature's queries. Grouping usage
 * by service is a rollup over this map rather than a stored column, so a feature
 * can never disagree with its own service.
 */
export const BIGQUERY_USAGE_SERVICES = {
    Workspace: 'workspace',
    OpportunitiesEngine: 'opportunities-engine',
    DataSync: 'data-sync',
    DashboardKpis: 'dashboard-kpis',
    CorePersonalities: 'core-personalities',
} as const;

export type BigQueryUsageService =
    (typeof BIGQUERY_USAGE_SERVICES)[keyof typeof BIGQUERY_USAGE_SERVICES];

export const BIGQUERY_USAGE_SERVICE_VALUES = Object.values(
    BIGQUERY_USAGE_SERVICES,
) as BigQueryUsageService[];

/**
 * A shared package's features are owned by the package, not by whichever worker
 * happens to load it: `dashboard-kpis` queries run inside the workspace worker,
 * and attributing them to `workspace` would hide the package's own spend behind
 * the page that triggered it.
 */
export const BIGQUERY_USAGE_FEATURE_SERVICES: Record<BigQueryUsageFeature, BigQueryUsageService> = {
    [BIGQUERY_USAGE_FEATURES.DashboardMyBrand]: BIGQUERY_USAGE_SERVICES.Workspace,
    [BIGQUERY_USAGE_FEATURES.OverviewMyBrand]: BIGQUERY_USAGE_SERVICES.Workspace,
    [BIGQUERY_USAGE_FEATURES.OverviewCompetitors]: BIGQUERY_USAGE_SERVICES.Workspace,
    [BIGQUERY_USAGE_FEATURES.OverviewTopicDetail]: BIGQUERY_USAGE_SERVICES.Workspace,
    [BIGQUERY_USAGE_FEATURES.OverviewCompetitorTopicDetail]: BIGQUERY_USAGE_SERVICES.Workspace,
    [BIGQUERY_USAGE_FEATURES.DrilldownMyBrand]: BIGQUERY_USAGE_SERVICES.Workspace,
    [BIGQUERY_USAGE_FEATURES.DrilldownMyBrandDrawer]: BIGQUERY_USAGE_SERVICES.Workspace,
    [BIGQUERY_USAGE_FEATURES.DrilldownCompetitors]: BIGQUERY_USAGE_SERVICES.Workspace,
    [BIGQUERY_USAGE_FEATURES.DrilldownCreativeHash]: BIGQUERY_USAGE_SERVICES.Workspace,
    [BIGQUERY_USAGE_FEATURES.WorkspaceOptions]: BIGQUERY_USAGE_SERVICES.Workspace,
    [BIGQUERY_USAGE_FEATURES.SetGeneration]: BIGQUERY_USAGE_SERVICES.OpportunitiesEngine,
    [BIGQUERY_USAGE_FEATURES.ScriptGeneration]: BIGQUERY_USAGE_SERVICES.OpportunitiesEngine,
    [BIGQUERY_USAGE_FEATURES.CreativesGeneration]: BIGQUERY_USAGE_SERVICES.OpportunitiesEngine,
    [BIGQUERY_USAGE_FEATURES.PersonaOpportunities]: BIGQUERY_USAGE_SERVICES.OpportunitiesEngine,
    [BIGQUERY_USAGE_FEATURES.DataSyncProducts]: BIGQUERY_USAGE_SERVICES.DataSync,
    [BIGQUERY_USAGE_FEATURES.DataSyncProductTypes]: BIGQUERY_USAGE_SERVICES.DataSync,
    [BIGQUERY_USAGE_FEATURES.DataSyncIndustries]: BIGQUERY_USAGE_SERVICES.DataSync,
    [BIGQUERY_USAGE_FEATURES.DataSyncPersonas]: BIGQUERY_USAGE_SERVICES.DataSync,
    [BIGQUERY_USAGE_FEATURES.DataSyncIndustryPersonas]: BIGQUERY_USAGE_SERVICES.DataSync,
    [BIGQUERY_USAGE_FEATURES.DataSyncFeaturesMappings]: BIGQUERY_USAGE_SERVICES.DataSync,
    [BIGQUERY_USAGE_FEATURES.BrandBrain]: BIGQUERY_USAGE_SERVICES.DataSync,
    [BIGQUERY_USAGE_FEATURES.DashboardKpisMyBrand]: BIGQUERY_USAGE_SERVICES.DashboardKpis,
    [BIGQUERY_USAGE_FEATURES.DashboardKpisCompetitors]: BIGQUERY_USAGE_SERVICES.DashboardKpis,
    [BIGQUERY_USAGE_FEATURES.DashboardKpisDefaultFilters]: BIGQUERY_USAGE_SERVICES.DashboardKpis,
    [BIGQUERY_USAGE_FEATURES.DashboardKpisIndustryProducts]: BIGQUERY_USAGE_SERVICES.DashboardKpis,
    [BIGQUERY_USAGE_FEATURES.CorePersonalities]: BIGQUERY_USAGE_SERVICES.CorePersonalities,
};

export function serviceForBigQueryUsageFeature(feature: BigQueryUsageFeature): BigQueryUsageService {
    return BIGQUERY_USAGE_FEATURE_SERVICES[feature];
}

/**
 * Tenant id recorded for work that genuinely belongs to no tenant — the
 * platform-wide data-sync pipelines. Distinct from a null tenant, which means
 * the call site has a tenant that has not been plumbed through yet; collapsing
 * the two would make the tenant breakdown wrong by an unstated amount.
 *
 * Cannot collide with a real tenant id: those are cuids, which are lowercase
 * alphanumeric and contain no underscores.
 */
export const BIGQUERY_USAGE_PLATFORM_TENANT = '__platform__';
